import { Show, useObservable } from "@legendapp/state/react"
import { Pressable, View, TextInput, Platform, Modal } from "react-native"
import Icon from "@react-native-vector-icons/material-design-icons"
import { trpc } from "utils/trpc"
import { useState } from "react"
import { List } from "./list"
import { useDebounce } from "use-debounce"
import { ar } from "i18n/ar"
import { Text } from "./core"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { cn, sharedStyles } from "@src/utils"

type Props = {
  value: number | undefined
  onSelectionChange: (id: number | undefined) => void
  className?: string
}

export function EmployeePicker({ value, className, onSelectionChange }: Props) {
  const employees = trpc.employees.list.useQuery()
  const visible = useObservable(false)

  const selectedEmployee = employees.data?.find((employee) => Number(employee.id) === value)

  function handlePress() {
    visible.set(true)
  }

  function hide() {
    visible.set(false)
  }

  return (
    <>
      <Pressable className={cn("h-11 bg-gray-50 border border-gray-300 rounded px-4 justify-center active:opacity-50", className)} onPress={handlePress}>
        <Text className="text-gray-500 text-left">{selectedEmployee?.name ?? selectedEmployee?.username ?? ar.selectEmployee}</Text>
      </Pressable>

      <Show if={visible}>
        <EmployeePickModal value={value} onSelectionChange={onSelectionChange} hide={hide} />
      </Show>
    </>
  )
}

function EmployeePickModal({ value, onSelectionChange, hide }: Props & { hide: () => void }) {
  const employees = trpc.employees.list.useQuery()
  const [searchText, setSearchText] = useState("")
  const [debouncedSearchText] = useDebounce(searchText, 500)
  const insets = useSafeAreaInsets()

  const filteredEmployees = employees.data?.filter((employee) => employee.username.toLowerCase().includes(debouncedSearchText.toLowerCase())) || []

  return (
    <Modal animationType="slide" presentationStyle="formSheet" allowSwipeDismissal onRequestClose={hide}>
      <View className="flex-row py-3 pl-2 items-center justify-between border-b-2 mb-4" style={{ marginTop: Platform.OS === "android" ? insets.top : 0 }}>
        <Text className="font-semibold text-2xl text-gray-800 text-left">{ar.employees}</Text>
        <Pressable onPress={hide} className="px-2 active:opacity-70">
          <Icon name="close" size={25} />
        </Pressable>
      </View>

      <View className="mx-4 mb-2">
        <TextInput
          className="h-10 bg-gray-50 border border-gray-300 rounded px-3 text-sm text-right"
          placeholder={ar.searchEmployees}
          value={searchText}
          onChangeText={setSearchText}
        />
      </View>

      <List
        data={filteredEmployees}
        keyExtractor={(item) => item.id.toString()}
        contentContainerStyle={sharedStyles.contentContainer}
        renderItem={({ item: employee }) => {
          return (
            <Pressable
              className="py-3 px-4 border-b border-gray-300"
              onPress={() => {
                onSelectionChange(Number(employee.id))
                hide()
              }}
            >
              <Text className={`text-left ${value === Number(employee.id) ? "text-blue-600" : ""}`}>{employee.name ?? employee.username}</Text>
            </Pressable>
          )
        }}
      />
    </Modal>
  )
}
